/* istanbul ignore file */

import { Location } from '@angular/common';
import { EnvironmentProviders, inject, InjectionToken, Injector, provideAppInitializer, Provider } from '@angular/core';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { AzureOAuthOptions } from '@libs/shared/models';
import { OAuthConfigService } from '../../oauth-config.service';

export const LL_DIRECTORY_USER_MSAL_INSTANCE: InjectionToken<IPublicClientApplication> = new InjectionToken('LL_DIRECTORY_USER_MSAL_INSTANCE');
export const LL_DIRECTORY_USER_MSAL_SERVICE: InjectionToken<MsalService> = new InjectionToken('LL_DIRECTORY_USER_MSAL_SERVICE');
export const LL_DIRECTORY_USER_MSAL_GUARD_CONFIG: InjectionToken<MsalGuardConfiguration> = new InjectionToken('LL_DIRECTORY_USER_MSAL_GUARD_CONFIG');

export const generalAuthProviders: Provider[] = [
  MsalBroadcastService,
];

export const llDirectoryUserMsalAuthProviders: (Provider | EnvironmentProviders)[] = [
  {
    provide: LL_DIRECTORY_USER_MSAL_INSTANCE,
    useFactory: (configService: OAuthConfigService): IPublicClientApplication => {
      const options: AzureOAuthOptions = configService.getLlDirectoryUserAzureOAuthOptions();

      return new PublicClientApplication({
        auth: {
          clientId: options.clientId,
          authority: `https://${options.authorityHostname}/${options.tenantName}/${options.signInPolicy}/`,
          redirectUri: options.redirectUri,
          postLogoutRedirectUri: options.redirectUri,
          knownAuthorities: [options.authorityHostname],
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
          loggerOptions: {
            logLevel: LogLevel.Info,
            piiLoggingEnabled: false,
          },
        },
      });
    },
    deps: [OAuthConfigService],
  },
  {
    provide: LL_DIRECTORY_USER_MSAL_SERVICE,
    useFactory: (pca: IPublicClientApplication, location: Location, injector: Injector) =>
      new MsalService(pca, location, injector),
    deps: [LL_DIRECTORY_USER_MSAL_INSTANCE, Location, Injector],
  },
  {
    provide: LL_DIRECTORY_USER_MSAL_GUARD_CONFIG,
    useFactory: (configService: OAuthConfigService): MsalGuardConfiguration => {
      const options: AzureOAuthOptions = configService.getLlDirectoryUserAzureOAuthOptions();

      return {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: [...options.scope],
        },
        loginFailedRoute: '/ll-directory-auth',
      };
    }, deps: [OAuthConfigService],
  },
  provideAppInitializer(() => {
    const msal: MsalService = inject<MsalService>(LL_DIRECTORY_USER_MSAL_SERVICE);

    return msal.instance.initialize();
  }),
];

export const userMsalAuthProviders: (Provider | EnvironmentProviders)[] = [
  {
    provide: MSAL_INSTANCE,
    useFactory: (configService: OAuthConfigService): IPublicClientApplication => {
      const options: AzureOAuthOptions = configService.getUserAzureOAuthOptions();

      return new PublicClientApplication({
        auth: {
          clientId: options.clientId,
          authority: `https://${options.authorityHostname}/tfp/${options.tenantName}/${options.signInPolicy}/`,
          redirectUri: options.redirectUri,
          postLogoutRedirectUri: options.redirectUri,
          knownAuthorities: [options.authorityHostname],
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
          loggerOptions: {
            logLevel: LogLevel.Info,
            piiLoggingEnabled: false,
          },
        },
      });
    },
    deps: [OAuthConfigService],
  },
  {
    provide: MsalService,
    useFactory: (pca: IPublicClientApplication, location: Location, injector: Injector) =>
      new MsalService(pca, location, injector),
    deps: [MSAL_INSTANCE, Location, Injector],
  },
  {
    provide: MSAL_GUARD_CONFIG,
    useFactory: (configService: OAuthConfigService): MsalGuardConfiguration => {
      const options: AzureOAuthOptions = configService.getUserAzureOAuthOptions();

      return {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: [...options.scope],
        },
        loginFailedRoute: '/auth',
      };
    }, deps: [OAuthConfigService],
  },
  provideAppInitializer(() => {
    const msal: MsalService = inject(MsalService);

    return msal.instance.initialize();
  }),
];

export function getUserAuthority(config: AzureOAuthOptions, policy: string): string {
  return 'https://' + config.authorityHostname + '/tfp/' + config.tenantName + '/' + policy + '/';
}

export function getLlDirectoryUserAuthority(config: AzureOAuthOptions, policy: string): string {
  return 'https://' + config.authorityHostname + '/' + config.tenantName + '/' + policy + '/';
}
